/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Button,
    Flex,
    Heading1,
    Heading2,
    IconButton,
    IconControlledSubstance,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import {
    useDisclosure,
    useIsMobile,
} from '@myphx-engineering/phx-design-system/hooks';

import { RxForUser } from '../../../components/cabinet/rx-for-user/RxForUser';
import { DrugCoverageAlerts } from '../../../components/common/drug-coverage-alerts/DrugCoverageAlerts';
import { NotCoveredModal } from '../../../components/common/drug-coverage-alerts/NotCoveredModal';
import { PARequiredModal } from '../../../components/common/drug-coverage-alerts/PARequiredModal';
import { PrescriptionCancelledAlert } from '../../../components/common/PrescriptionCancelledAlert';
import { Prescriber } from '../../../components/prescriber/Prescriber';
import { PriorAuthLastActivityCard } from '../../../components/prior-authorization/components/PriorAuthLastActivityCard';
import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import { getFragment } from '../../../graphql';
import {
    type CoverageFlags,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    type ProviderOffersInfoFragment,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { getAbsoluteRoute } from '../../../util';

import { LastOrderActivityV2 } from './LastOrderActivityV2';
import { PrescriberOrderDetailsV2 } from './PrescriberOrderDetailsV2';

type OrderDetailsV2Props = {
    offer?: ProviderOffersInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    coverageFlags?: CoverageFlags;
};

export const OrderDetailsV2 = ({
    offer,
    prescriberOrder,
    coverageFlags,
}: OrderDetailsV2Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { helpCenterMedicationEnabled } = useFlags();
    const isMobile = useIsMobile();
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);
    const [isNotCoveredModalOpen, { toggle: toggleNotCoveredModal }] =
        useDisclosure(false);
    const { setBackOverrideState } = useNavBar();

    useEffect(() => {
        if (prescriberOrder.latestSelectedOffer) {
            setBackOverrideState({
                overrideFn: () => {
                    navigate(getAbsoluteRoute('cabinet.history'));
                },
            });
        }

        return () => setBackOverrideState({ overrideFn: null });
    }, [prescriberOrder.latestSelectedOffer, navigate, setBackOverrideState]);

    const pharmacyProvider = getFragment(
        getFragment(prescriberOrder.latestSelectedOffer)?.pharmacyProvider
    );
    const prescriptionId = prescriberOrder.drxId ?? prescriberOrder.id;
    const rxPatient = getFragment(prescriberOrder?.patient);
    const controlledSubstanceCode = getFragment(
        prescriberOrder.authorizedMedication
    ).controlledSubstanceCode;

    // Temporary until VELA integration
    const isInstantRouting = false;

    const shouldShowOrderDetails =
        !controlledSubstanceCode ||
        isInstantRouting ||
        !!(controlledSubstanceCode && !prescriberOrder.latestSelectedOffer);

    const applicablePriorAuthorization = getFragment(
        prescriberOrder.applicablePriorAuthorization
    );

    const isApproved = applicablePriorAuthorization
        ? usePriorAuthorizationDetails(applicablePriorAuthorization)
              .isApprovedPriorAuthorization
        : false;

    const { isApprovedPriorAuthorization } = usePriorAuthorizationDetails(
        applicablePriorAuthorization
    );

    // Note: Allow Fill when it's an approved non-specialty dRx PA or just new dRx
    const showFillButton =
        (isApprovedPriorAuthorization &&
            !applicablePriorAuthorization?.isSpecialty &&
            prescriberOrder.status !== PrescriberOrderStatus.SentToPharmacy) ||
        prescriberOrder.status === PrescriberOrderStatus.New;

    return (
        <Stack gap="lg" flex="1">
            <Stack gap="xl" flex={isMobile ? '1' : '0'}>
                <Stack gap="lg">
                    <PrescriptionCancelledAlert
                        prescriberOrder={prescriberOrder}
                    />
                    <Stack gap="xs">
                        <RxForUser patient={rxPatient} />
                        <Flex align="center" gap="xxs">
                            <Heading1>
                                {prescriberOrder.productDescription}
                            </Heading1>
                            {controlledSubstanceCode ? (
                                <IconButton
                                    Icon={IconControlledSubstance}
                                    withStyles={false}
                                    onClick={
                                        helpCenterMedicationEnabled
                                            ? () =>
                                                  navigate(
                                                      getAbsoluteRoute(
                                                          'helpCenter.medications'
                                                      )
                                                  )
                                            : undefined
                                    }
                                />
                            ) : null}
                        </Flex>
                        {coverageFlags && !isApproved ? (
                            <DrugCoverageAlerts
                                coverageFlags={coverageFlags}
                                togglePARequiredModal={togglePARequiredModal}
                                toggleNotCoveredModal={toggleNotCoveredModal}
                            />
                        ) : null}
                        <PARequiredModal
                            opened={isPARequiredModalOpen}
                            close={togglePARequiredModal}
                        />
                        <NotCoveredModal
                            opened={isNotCoveredModalOpen}
                            close={toggleNotCoveredModal}
                        />
                    </Stack>
                    <ConditionalComponent condition={shouldShowOrderDetails}>
                        <PrescriberOrderDetailsV2
                            showRxImage
                            showQuantity
                            prescriberOrder={prescriberOrder}
                            offer={offer}
                            showPrescriber={false}
                        />
                    </ConditionalComponent>
                    {prescriberOrder.applicablePriorAuthorization ? (
                        <PriorAuthLastActivityCard
                            priorAuthorization={getFragment(
                                prescriberOrder.applicablePriorAuthorization
                            )}
                            prescriberOrderId={prescriptionId}
                            pharmacyProvider={pharmacyProvider}
                        />
                    ) : (
                        <LastOrderActivityV2
                            prescriberOrder={prescriberOrder}
                            offer={offer}
                        />
                    )}
                </Stack>
                <Stack gap="sm">
                    <Heading2>
                        {t('orderConfirmation.prescriber.subHeader')}
                    </Heading2>
                    <Prescriber
                        prescriber={getFragment(prescriberOrder.prescriber)}
                    />
                </Stack>
            </Stack>
            <ConditionalComponent condition={showFillButton}>
                <Button
                    onClick={() =>
                        navigate(
                            getAbsoluteRoute('cabinet.rxId.root', {
                                prescriptionId,
                            })
                        )
                    }
                >
                    {t('orderDetails.fillPrescription')}
                </Button>
            </ConditionalComponent>
        </Stack>
    );
};
